import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Clock, Calculator, Calendar } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './WorkHoursCalculatorPage/components/CalculationForm';
import ResultsOverview from './WorkHoursCalculatorPage/components/ResultsOverview';
import WorkScheduleTable from './WorkHoursCalculatorPage/components/WorkScheduleTable';
import QuickAdjustmentSidebar from './WorkHoursCalculatorPage/components/QuickAdjustmentSidebar';
import SavedCalculationsSidebar from './WorkHoursCalculatorPage/components/SavedCalculationsSidebar';
import InfoContent from './WorkHoursCalculatorPage/components/InfoContent';
import HolidaysInfoSidebar from './WorkHoursCalculatorPage/components/HolidaysInfoSidebar';

// Импорт хелперов
import { getDatesBetween, getWorkingDaysBetween } from './WorkHoursCalculatorPage/components/helpers/dateHelpers';

// Определение начального состояния формы
export const initialFormData = {
  periodType: 'month', // month, year, custom
  year: String(new Date().getFullYear()), // Текущий год как строка
  month: String(new Date().getMonth()), // Текущий месяц (0-11) как строка
  startDate: new Date().toISOString().split('T')[0], // Формат YYYY-MM-DD
  endDate: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString().split('T')[0],
  workScheduleType: 'standard', // standard (5/2), shift, custom
  workDaysPerWeek: '5',
  workHoursPerDay: '8',
  includeBreaks: true,
  breakTimeMinutes: '60',
  includeHolidays: true,
  customHolidays: [], // {date: YYYY-MM-DD, name: string, type: 'holiday'|'shortened'}
  nightShifts: false,
  nightShiftStartHour: '22',
  nightShiftEndHour: '6',
  nightShiftRate: '1.2', // Коэффициент доплаты за ночные часы
  overtimeHours: '0',
  overtimeRate: '1.5', // Коэффициент доплаты за сверхурочные часы
  weekendWork: '0',
  weekendRate: '2.0', // Коэффициент доплаты за работу в выходные
  hourlyRate: '250', // Почасовая ставка для расчета зарплаты
  vacationDays: '0', // Дни отпуска
  sickLeaveDays: '0', // Дни больничного
  shiftPattern: '5/2', // Для сменного графика (5/2, 2/2, 1/3, custom)
  useProductionCalendar: true, // Использовать производственный календарь РФ
};

// Типы графиков работы
export const workScheduleTypes = [
  { id: 'standard', name: 'Стандартный (5/2)', description: '5 рабочих дней, 2 выходных' },
  { id: 'shift', name: 'Сменный график', description: 'Работа по сменам' },
  { id: 'custom', name: 'Произвольный график', description: 'Настраиваемый график работы' }
];

// Шаблоны сменных графиков
export const shiftPatterns = [
  { id: '5/2', name: '5/2', description: '5 дней работы, 2 выходных' },
  { id: '2/2', name: '2/2', description: '2 дня работы, 2 выходных' },
  { id: '1/3', name: '1/3', description: '1 день работы, 3 выходных' },
  { id: '3/1', name: '3/1', description: '3 дня работы, 1 выходной' },
  { id: '4/3', name: '4/3', description: '4 дня работы, 3 выходных' },
  { id: '7/7', name: '7/7', description: 'Неделя через неделю' },
  { id: '15/15', name: '15/15', description: '15 дней работы, 15 выходных' },
  { id: 'custom', name: 'Произвольный', description: 'Настраиваемый сменный график' }
];

/**
 * WorkHoursCalculatorPage Component - Калькулятор рабочего времени с расчетом рабочих часов, смен
 * и заработной платы с учетом российского трудового законодательства.
 */
const WorkHoursCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('work-hours-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор рабочего времени | Gensio';
  const metaDescription = tool?.metaDescription || 'Калькулятор рабочего времени: расчет количества рабочих часов, смен и зарплаты с учетом российского трудового законодательства. Учет праздников, ночных смен и сверхурочных.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [workSchedule, setWorkSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalculationsData = localStorage.getItem('savedWorkHoursCalculations');
    if (savedCalculationsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalculationsData));
      } catch (e) {
        console.error("Ошибка загрузки сохраненных расчетов:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'monthly':
          newState.periodType = 'month';
          break;
        case 'yearly':
          newState.periodType = 'year';
          break;
        case 'custom-period':
          newState.periodType = 'custom';
          break;
        case 'shift-work':
          newState.workScheduleType = 'shift';
          break;
        case 'night-shifts':
          newState.workScheduleType = 'shift';
          newState.nightShifts = true;
          break;
        case 'hourly-rate':
          // Стандартный вариант с почасовой оплатой
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setWorkSchedule([]);
    setErrors({});
    setShowFullSchedule(false);

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `Расчет ${new Date().toLocaleDateString('ru-RU')}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      periodType: formData.periodType
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedWorkHoursCalculations', JSON.stringify(updatedSavedCalculations));

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedWorkHoursCalculations', JSON.stringify(updatedSavedCalculations));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calculationToLoad = savedCalculations.find(calc => calc.id === id);
    if (calculationToLoad) {
      setFormData(calculationToLoad.formData);
      setResults(calculationToLoad.results);
      // В реальном приложении нужно также загрузить график работы
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Clock className="w-8 h-8 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор рабочего времени'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              workSchedule={workSchedule}
              setWorkSchedule={setWorkSchedule}
              workScheduleTypes={workScheduleTypes}
              shiftPatterns={shiftPatterns}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <WorkScheduleTable 
                  workSchedule={workSchedule} 
                  results={results}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                />
              </>
            )}

            {/* --- Информационный контент о рабочем времени --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сохраненных расчетов --- */}
            <SavedCalculationsSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель информации о праздниках --- */}
            <HolidaysInfoSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-cyan-600 focus:ring-cyan-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-cyan-600 focus:ring-cyan-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Work Schedule Table */
        .work-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .work-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .work-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .work-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .work-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .work-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .work-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .work-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .work-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default WorkHoursCalculatorPage;